/* eslint-disable @typescript-eslint/no-unused-vars */
import { canUseDOM, addSubDirectoryPrefix, getWindowSession, getViewPortValue } from '../../utils/helper';
import { URL_CONSTANTS, constants } from '../../constants';

const { RRD_GI_URL, RRD_URL, RLM_URL, MODIFY_RLM_PAGE_URL } = URL_CONSTANTS;
const { MOBILE, TABLET, FLOW, QRV } = constants;

// Create a mapper for modalId and flyoutId based on type
const modalFlyoutMapper = {
  member: {
    modal: constants.SIGNIN_MODAL_TYPE_MEMBER,
    flyout: constants.SIGNIN_FLYOUT_TYPE_MEMBER,
  },
  nonMember: {
    modal: constants.SIGNIN_MODAL_TYPE_NON_MEMBER,
    flyout: constants.SIGNIN_FLYOUT_TYPE_NON_MEMBER,
  },
  points: {
    modal: constants.SIGNIN_MODAL_TYPE_POINTS,
    flyout: constants.SIGNIN_FLYOUT_TYPE_POINTS,
  },
  qrv: {
    modal: constants.SIGNIN_TYPE_QRV,
    flyout: constants.SIGNIN_TYPE_QRV,
  },
};

// Create a mapper for modalId and flyoutId based on type
const dttModalConfigMapper = {
  member: {
    modal: constants.DTT_SIGNIN_MODAL_TYPE_MEMBER,
  },
  nonMember: {
    modal: constants.DTT_SIGNIN_MODAL_TYPE_NON_MEMBER,
  },
  points: {
    modal: constants.DTT_SIGNIN_MODAL_TYPE_POINTS,
  },
  qrv: {
    modal: constants.SIGNIN_TYPE_QRV,
  },
};

export const determineModalType = ({
  isDTT,
  isFlyout,
  type,
}: {
  isDTT?: boolean;
  isFlyout?: boolean;
  type: 'member' | 'nonMember' | 'points' | 'qrv';
}) => {
  const modalFlyout = modalFlyoutMapper[type];
  const dttModalConfig = dttModalConfigMapper[type];
  return isFlyout ? modalFlyout?.flyout : isDTT ? dttModalConfig?.modal : modalFlyout?.modal;
};

/**
 * Generates event action data based on parameters.
 * @param pageType The type of the page.
 * @param isRedemptionSelected Indicates if redemption is selected.
 * @param selectRateUrl The URL for selecting rates.
 * @param memberRate Indicates if the rate is for a member.
 * @returns Event action data object.
 */
export const eventActionData = (
  pageType?: string,
  isRedemptionSelected?: boolean,
  selectRateUrl?: string,
  isDTT?: boolean,
  memberRate?: boolean,
  eventNameExt?: string,
  isFlyout?: boolean,
  rateRestrictedInd?: boolean,
  qrvVariation?: string,
  isRememberUser?: boolean
) => {
  // Check if member rate is defined, if not, retrieve from session object
  let isMemberRate = memberRate;
  if (isMemberRate === undefined) {
    const sessionObject = getWindowSession();
    isMemberRate = sessionObject?.cacheData?.data?.AriesReservation?.isMemberRate;
  }
  const memberModal = determineModalType({ isDTT: isDTT, isFlyout: isFlyout, type: 'member' });

  const nonMemberModal = determineModalType({ isDTT: isDTT, isFlyout: isFlyout, type: 'nonMember' });
  // Determine modal type based on member rate
  const modalType = isMemberRate ? memberModal : nonMemberModal;
  // Check if running in a browser environment
  const defaultEvent = {
    modalType: '',
    successEvent: eventNameExt ? `${constants.SIGNIN_SUCCESS_EVENT}${eventNameExt}` : constants.SUCCESS_SIGNIN_EVENT,
    signInReturnUrl: null,
    isFlyout,
  };

  const getEventData = (
    modalType: string,
    signInReturnUrl?: string,
    additionalProps?: {
      flyoutType?: string;
      qrvVariation?: string;
      newApproach?: boolean;
      isSwap?: boolean;
      trackingPageName?: string;
    }
  ) => {
    return {
      ...defaultEvent,
      modalType,
      signInReturnUrl,
      ...additionalProps,
    };
  };

  if (canUseDOM) {
    const baseUrl = selectRateUrl || RLM_URL;
    const signInReturnUrl = decodeURIComponent(encodeURIComponent(addSubDirectoryPrefix(baseUrl)));

    const joinNowBaseUrl =
      pageType === constants.RLM_PAGE ? addSubDirectoryPrefix(RLM_URL) : addSubDirectoryPrefix(MODIFY_RLM_PAGE_URL);
    const joinNowUrlObj = new URL(joinNowBaseUrl, window.location.origin);
    // Determine event action data based on page type
    if (pageType === constants.RLM_PAGE && isRedemptionSelected) {
      // For RLM page with redemption selected
      return getEventData(determineModalType({ isDTT: isDTT, isFlyout: isFlyout, type: 'points' }), signInReturnUrl);
    } else if (
      (pageType === constants.RLM_PAGE || pageType === constants.MODIFY_RLM) &&
      rateRestrictedInd &&
      qrvVariation
    ) {
      joinNowUrlObj.searchParams.set(FLOW, QRV);
      const joinNowReturnUrl = decodeURIComponent(encodeURIComponent(joinNowUrlObj.toString()));

      // For RLM page with QRV EXPLORE and COPORATE Flow
      return getEventData(determineModalType({ isDTT: isDTT, isFlyout: isFlyout, type: 'qrv' }), joinNowReturnUrl, {
        flyoutType:
          qrvVariation === constants.EXPLORE
            ? constants.SIGNIN_FLYOUT_TYPE_QRV_EXPLORE
            : constants.SIGNIN_FLYOUT_TYPE_QRV_CORP,
        qrvVariation: qrvVariation?.toLowerCase() || '',
        newApproach: true,
        trackingPageName: qrvVariation?.toUpperCase(),
        ...(isRememberUser && qrvVariation === constants.CORP ? { isSwap: true } : {}),
      });
    } else if (pageType === constants.HYBRID_PAGE) {
      // For hybrid page
      return getEventData(
        modalType,
        decodeURIComponent(encodeURIComponent(window.location.pathname?.replace(RRD_GI_URL, RRD_URL))),
        isMemberRate && isFlyout ? { isSwap: true } : {} // Swap view for Anonymous + Member Rate on Hybird Page
      );
    } else if (pageType === constants.MODIFY_RLM) {
      // For MODIFY_RLM page
      return getEventData(memberModal);
    } else {
      // Default case
      return getEventData(modalType);
    }
  }
  // Return empty modal type if not running in a browser environment
  return {
    ...defaultEvent,
  };
};

// Determine what is the modal state should be based on page and user flow and rateType
export const determineModalState = (
  isHybridPage: boolean,
  isMemberRate: boolean,
  disableSignInAutoLoad: boolean,
  isRememberUser: boolean,
  screenViewport = getViewPortValue()
): boolean => {
  switch (true) {
    case !isHybridPage:
      return true;
    case isHybridPage:
      return false;
    case [MOBILE, TABLET].includes(screenViewport):
      return false;
    case isRememberUser:
      return false;
    case isMemberRate:
      return true;
    default:
      return !disableSignInAutoLoad;
  }
};

// Gives remember user state if its is or not
export const rememberedUser = (memState?: string) => {
  if (memState && memState?.toLowerCase() === constants.REMEMBERED_STATE) {
    return true;
  }

  return false;
};
